import { useEffect, useRef, useState } from "react";
import { useSettlementStore } from "../store";
import { BorderRectProps } from "../plugins/border-rect/dto";
import { getListBindsByRowRange } from "../utils";
import { generateId, generateRandomColor } from "../../utils";
import { LoopSelectProps } from "../plugins/loop-select/dto";
import { Field } from "../dto";

// 随机生成背景色
const generateLoopBgColor = (() => {
  const colors: string[] = [];
  const generate = () => {
    const color = generateRandomColor(0.2);
    if (!colors.includes(color)) {
      colors.push(color);
      return color;
    }
    return generate();
  };
  return generate;
})();

const useLoop = (fields: Omit<Field, "children">[]) => {
  const [{ config }, { setConfig }] = useSettlementStore();
  const tableRef = useRef<HTMLTableElement>();
  const [loopsDrag, setLoopsDrag] = useState<LoopSelectProps["items"]>([]);

  const [loopBorderRects, setLoopBorderRects] = useState<
    (Omit<BorderRectProps, "width"> & { id: string })[]
  >([]);

  const colorRef = useRef<Record<string, string>>({});

  const reset = () => {
    const { loops, rows } = config;
    // 段落
    const parts = new Map<number, { id?: string; count: number }>();
    for (let i = 0; i < rows.length; i++) {
      if (rows[i].cells.length > 0) {
        parts.set(i, {
          count: 1,
        });
      }
    }
    for (const loop of loops) {
      // 已设置循环段落
      parts.set(loop.minRow, {
        id: loop.id,
        count: loop.maxRow - loop.minRow + 1,
      });
    }
    // 排序
    const operations = Array.from(parts.entries()).sort((a, b) => b[0] - a[0]);
    // 每一段占行及高度
    const items: LoopSelectProps["items"] = config.rows.map((i, idx) => ({
      minRow: idx,
      maxRow: idx,
      height:
        tableRef.current
          ?.querySelector(`tr[data-id="${i.id}"]`)
          ?.getBoundingClientRect()?.height ?? i.height,
    }));
    const newLoopBorderRects: typeof loopBorderRects = [];
    for (const [startIndex, { id, count }] of operations) {
      if (startIndex < 0 || startIndex >= items.length) {
        console.warn(
          `【TableEditor】索引 ${startIndex} 超出数组范围 [0, ${
            items.length - 1
          }]`
        );
        continue;
      }

      const endIndex = Math.min(startIndex + count, items.length);
      const height = items
        .slice(startIndex, endIndex)
        .reduce((a, b) => a + b.height, 0);
      const y = items.slice(0, startIndex).reduce((a, b) => a + b.height, 0); // 获取段落中所有list字段绑定的父code
      const { parentCodes } = getListBindsByRowRange(
        startIndex,
        startIndex + count - 1,
        config,
        fields
      );
      // 没有父级code，即未设置循环字段
      const disabled = parentCodes.length === 0;
      let tooltip = "选择多行数据设置";
      if (id) {
        tooltip = "取消多行数据设置";
      } else if (disabled) {
        tooltip = "未设置多行数据字段，无法设置";
      }
      // 替换原数据：删除被累加的元素，插入累加结果
      items.splice(startIndex, endIndex - startIndex, {
        minRow: startIndex,
        maxRow: startIndex + count - 1,
        height,
        loop: id,
        tooltip,
        disabled,
      });
      // 已有循环
      if (id) {
        if (!colorRef.current[id]) {
          colorRef.current[id] = generateLoopBgColor();
        }
        newLoopBorderRects.push({
          id,
          x: 0,
          y,
          height,
          color: "transparent",
          visible: true,
          bg: colorRef.current[id],
        });
      }
    }
    setLoopsDrag(items);
    setLoopBorderRects(newLoopBorderRects);
  };

  // 设置循环
  const onLoopChange = (type: "add" | "remove", idxs: number[]) => {
    if (type === "remove") {
      return setConfig((cfg) => {
        const idx = cfg.loops?.findIndex(
          (i) => i.id === loopsDrag[idxs?.[0]]?.loop
        );
        if (idx >= 0) {
          cfg.loops?.splice(idx, 1);
        }
      });
    }
    if (type === "add") {
      const minRow = loopsDrag[idxs?.[0]]?.minRow;
      const maxRow = loopsDrag[idxs?.[idxs.length - 1]]?.maxRow;
      return setConfig((cfg) => {
        cfg.loops.push({
          minRow,
          maxRow,
          id: generateId(),
        });
      });
    }
  };

  // 可能出现宽度小于内容出现换行导致的高度变化，因此需要通过dom获取高度&需要监听宽度变化
  useEffect(reset, [
    JSON.stringify(config.rows),
    JSON.stringify(config.cols),
    JSON.stringify(config.loops),
  ]);

  const setRef = (node) => {
    tableRef.current = node;
  };

  return [
    setRef,
    { items: loopsDrag, onChange: onLoopChange },
    loopBorderRects,
  ] as const;
};

export default useLoop;
